import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FondDePapier, SorteDePapier } from '@nextilt/commun';
import { api, EchecApi } from '../partage/api/client';
import type { ChangementDeLigne, ChangementDePapier, EnvoiDUnPapier, Papier, ReponsePapiers } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { papierPasEcrit } from '../partage/vocabulaire';
import { remettreAvant } from './glisserUnPapier';
import { avecLaLigne, laLigneNeuve, listeNeuve, sansLaLigne } from './reglesDeLaListe';
import { garderLeDernierFond, lireLeDernierFond, papiersMontres, postItNeuf } from './reglesDesPapiers';

/** Combien de temps « Annuler » reste après la croix : les mêmes six secondes qu'après une coche de la journée. */
export const DUREE_D_ANNULER_UN_PAPIER_MS = 6000;

const rangementDeLAppareil = () => window.localStorage;

/**
 * Les papiers de la bulle au centre de la carte, et leurs gestes (chantier post-it-et-listes,
 * ticket 02). Lus seulement quand ils se montrent (`actifs`) ; chaque geste pose la réponse du
 * serveur dans la lecture, sans rien relire d'autre : un papier ne change ni la feuille, ni
 * Maintenant.
 *
 * **La croix ne supprime pas tout de suite** : le papier se cache, et sa suppression part au
 * serveur quand « Annuler » s'en va (six secondes, un autre papier supprimé, ou la carte qu'on
 * quitte). Si la page se ferme pendant ce temps, rien n'est parti : le papier est encore là au
 * retour. Rien ne se perd, « Annuler » le rend tel quel (contenu, papier, place, taille, décor).
 */
export function usePapiers(noeudId: string, actifs: boolean) {
  const client = useQueryClient();
  const cle = cles.papiers(noeudId);
  const lecture = useQuery({
    queryKey: cle,
    queryFn: () => api.papiers(noeudId),
    enabled: actifs,
  });

  const [annonce, setAnnonce] = useState<string | null>(null);
  /** Le dernier posé, où le curseur se met. */
  const [aEcrire, setAEcrire] = useState<string | null>(null);

  /**
   * Le papier rendu par le serveur après un changement du papier lui-même (titre, texte, papier,
   * place, taille). Ses lignes restent celles de l'écran : elles n'ont qu'à elles leurs gestes
   * (ticket 04), et une réponse partie avant une coche ne la défait pas.
   */
  const remplacer = useCallback(
    (papier: Papier) =>
      client.setQueryData<ReponsePapiers>(cles.papiers(papier.noeudId), (avant) =>
        avant ? { papiers: avant.papiers.map((p) => (p.id === papier.id ? { ...papier, lignes: p.lignes } : p)) } : avant
      ),
    [client]
  );
  const dire = useCallback((erreur: unknown) => {
    setAnnonce(papierPasEcrit(erreur instanceof EchecApi ? erreur.code : 'erreur'));
  }, []);

  const poser = useMutation({
    mutationFn: (envoi: EnvoiDUnPapier) => api.poserUnPapier(noeudId, envoi),
    onSuccess: ({ papier }) => {
      client.setQueryData<ReponsePapiers>(cles.papiers(papier.noeudId), (avant) => ({
        papiers: [...(avant?.papiers ?? []), papier],
      }));
      setAnnonce(null);
      setAEcrire(papier.id);
    },
    onError: dire,
  });

  /**
   * Optimiste : le papier change tout de suite, et si le serveur refuse, seuls les champs envoyés
   * reviennent (ticket 03 : une place refusée revient, le texte écrit entre-temps reste).
   */
  const modifier = useMutation({
    mutationFn: ({ id, changement }: { id: string; changement: ChangementDePapier }) =>
      api.modifierUnPapier(id, changement),
    onMutate: async ({ id, changement }) => {
      await client.cancelQueries({ queryKey: cle });
      const avant = client.getQueryData<ReponsePapiers>(cle)?.papiers.find((p) => p.id === id);
      client.setQueryData<ReponsePapiers>(cle, (l) =>
        l ? { papiers: l.papiers.map((p) => (p.id === id ? { ...p, ...changement } : p)) } : l
      );
      return { avant };
    },
    onError: (erreur, { changement }, contexte) => {
      const avant = contexte?.avant;
      if (avant) {
        client.setQueryData<ReponsePapiers>(cle, (l) => (l ? { papiers: remettreAvant(l.papiers, avant, changement) } : l));
      }
      dire(erreur);
    },
    onSuccess: ({ papier }) => remplacer(papier),
  });

  const ecrire = useCallback(
    (id: string, changement: ChangementDePapier) => modifier.mutate({ id, changement }),
    [modifier]
  );

  /**
   * La place ou la taille d'un papier, au lâcher (ticket 03). Rend une promesse tenue quand le
   * serveur a répondu, oui ou non : le papier garde sa place tirée jusque-là, puis montre ce que
   * la lecture dit (la place gardée, ou celle d'avant si le serveur a refusé).
   */
  const placer = useCallback(
    (id: string, changement: ChangementDePapier): Promise<void> =>
      modifier.mutateAsync({ id, changement }).then(
        () => undefined,
        () => undefined
      ),
    [modifier]
  );

  /** Le papier tel que l'écran le montre, dans la lecture. */
  const lu = useCallback(
    (id: string) => client.getQueryData<ReponsePapiers>(cle)?.papiers.find((p) => p.id === id),
    [client, cle]
  );
  /**
   * Change un papier dans la lecture, sans rien envoyer. Une relecture en cours est d'abord
   * arrêtée, comme avant un changement du papier (`modifier`) : partie avant le geste, elle
   * rendrait la liste d'avant lui et l'effacerait de l'écran.
   */
  const changerDansLaLecture = useCallback(
    async (id: string, changer: (papier: Papier) => Papier) => {
      await client.cancelQueries({ queryKey: cle });
      client.setQueryData<ReponsePapiers>(cle, (l) =>
        l ? { papiers: l.papiers.map((p) => (p.id === id ? changer(p) : p)) } : l
      );
    },
    [client, cle]
  );

  /** Le dernier papier choisi est repris au post-it suivant ; une liste neuve est toujours à lignes. */
  const changerDeFond = useCallback(
    (id: string, fond: FondDePapier) => {
      if (lu(id)?.sorte === 'post_it') garderLeDernierFond(rangementDeLAppareil, fond);
      modifier.mutate({ id, changement: { fond } });
    },
    [modifier, lu]
  );

  // ── Les lignes d'une liste libre (ticket 04) : chaque geste se voit tout de suite ──

  /**
   * Ajoute une ligne vide à la fin ; la promesse rend son identifiant, pour y mettre le curseur
   * (`null` si le serveur a refusé, et le refus est dit).
   */
  const ajouterUneLigne = useCallback(
    async (papierId: string): Promise<string | null> => {
      const avant = lu(papierId);
      if (!avant) return null;
      try {
        const { papier } = await api.ajouterUneLigne(papierId, { texte: '' });
        const neuve = papier.lignes.find((l) => l.id === laLigneNeuve(avant, papier));
        if (!neuve) return null;
        await changerDansLaLecture(papierId, (p) => ({ ...p, lignes: [...p.lignes, neuve] }));
        setAnnonce(null);
        return neuve.id;
      } catch (erreur) {
        dire(erreur);
        return null;
      }
    },
    [lu, changerDansLaLecture, dire]
  );

  /** Écrire, cocher, décocher une ligne : tout de suite à l'écran ; refusé, elle reprend ce qu'elle avait. */
  const changerUneLigne = useCallback(
    (papierId: string, ligneId: string, changement: ChangementDeLigne) => {
      const avant = lu(papierId)?.lignes.find((l) => l.id === ligneId);
      if (!avant) return;
      void changerDansLaLecture(papierId, (p) => avecLaLigne(p, ligneId, changement));
      api.modifierUneLigne(papierId, ligneId, changement).then(
        ({ papier }) => {
          const rendue = papier.lignes.find((l) => l.id === ligneId);
          if (rendue) void changerDansLaLecture(papierId, (p) => avecLaLigne(p, ligneId, rendue));
        },
        (erreur: unknown) => {
          // Seuls les champs envoyés reviennent.
          const repris: ChangementDeLigne = {};
          if (changement.texte !== undefined) repris.texte = avant.texte;
          if (changement.cochee !== undefined) repris.cochee = avant.cochee;
          void changerDansLaLecture(papierId, (p) => avecLaLigne(p, ligneId, repris));
          dire(erreur);
        }
      );
    },
    [lu, changerDansLaLecture, dire]
  );

  /** Retirer une ligne : elle part de l'écran tout de suite ; refusé, la liste est relue. */
  const retirerUneLigne = useCallback(
    (papierId: string, ligneId: string) => {
      void changerDansLaLecture(papierId, (p) => sansLaLigne(p, ligneId));
      api.retirerUneLigne(papierId, ligneId).catch((erreur: unknown) => {
        void client.invalidateQueries({ queryKey: cle });
        dire(erreur);
      });
    },
    [changerDansLaLecture, client, cle, dire]
  );

  // ── La croix, et « Annuler » ──
  const [enSuppression, setEnSuppression] = useState<ReadonlySet<string>>(new Set());
  const enAttente = useRef<{ id: string; noeudId: string; minuteur: number } | null>(null);

  /** La suppression part au serveur ; le papier quitte la lecture une fois parti. */
  const partir = useCallback(
    (id: string, bulle: string) => {
      api
        .supprimerUnPapier(id)
        .then(() =>
          client.setQueryData<ReponsePapiers>(cles.papiers(bulle), (avant) =>
            avant ? { papiers: avant.papiers.filter((p) => p.id !== id) } : avant
          )
        )
        .catch(dire)
        .finally(() =>
          setEnSuppression((avant) => {
            const reste = new Set(avant);
            reste.delete(id);
            return reste;
          })
        );
    },
    [client, dire]
  );

  /** « Annuler » s'en va : ce qui attendait part. */
  const laisserPartir = useCallback(() => {
    const attente = enAttente.current;
    if (!attente) return;
    enAttente.current = null;
    window.clearTimeout(attente.minuteur);
    partir(attente.id, attente.noeudId);
  }, [partir]);

  /** Le papier qu'« Annuler » rendrait, tant qu'il est à l'écran. */
  const [annulable, setAnnuler] = useState<string | null>(null);
  useEffect(() => {
    if (!annulable) return;
    const minuteur = window.setTimeout(() => setAnnuler(null), DUREE_D_ANNULER_UN_PAPIER_MS);
    return () => window.clearTimeout(minuteur);
  }, [annulable]);

  /** La sorte du dernier papier supprimé : « Post-it supprimé » ou « Liste supprimée ». */
  const [sorteSupprimee, setSorteSupprimee] = useState<SorteDePapier>('post_it');

  const supprimer = useCallback(
    (id: string) => {
      laisserPartir();
      setSorteSupprimee(lu(id)?.sorte ?? 'post_it');
      setEnSuppression((avant) => new Set(avant).add(id));
      setAnnonce(null);
      enAttente.current = { id, noeudId, minuteur: window.setTimeout(laisserPartir, DUREE_D_ANNULER_UN_PAPIER_MS) };
      setAnnuler(id);
    },
    [laisserPartir, noeudId, lu]
  );

  const annuler = useCallback(() => {
    const attente = enAttente.current;
    if (!attente) return;
    enAttente.current = null;
    window.clearTimeout(attente.minuteur);
    setEnSuppression((avant) => {
      const reste = new Set(avant);
      reste.delete(attente.id);
      return reste;
    });
    setAnnuler(null);
  }, []);

  // On quitte la carte (une autre bulle, une autre vue) : « Annuler » n'est plus à portée, la suppression part.
  useEffect(() => () => laisserPartir(), [laisserPartir]);

  return {
    papiers: papiersMontres(lecture.data?.papiers ?? [], enSuppression),
    poserUnPostIt: () => poser.mutate(postItNeuf(lireLeDernierFond(rangementDeLAppareil))),
    poserUneListe: () => poser.mutate(listeNeuve()),
    pose: poser.isPending,
    aEcrire,
    ecrire,
    placer,
    changerDeFond,
    ajouterUneLigne,
    changerUneLigne,
    retirerUneLigne,
    supprimer,
    /** La sorte du papier qu'« Annuler » rendrait, tant qu'il est à l'écran. */
    annulable: annulable !== null ? sorteSupprimee : null,
    annuler,
    annonce,
  };
}
